import { AxiosResponse } from "axios";
import i18next from "i18next";

import { IApiError, IApiErrorResponse } from "../@types/ApiError";

/**
 * Check if the response indicates an error (status >= 400)
 */
export function isErrorResponse(response: AxiosResponse): boolean {
  return response.status >= 400;
}

/**
 * Extract errors array from an AxiosResponse
 */
export function extractErrors(response: AxiosResponse): IApiError[] {
  const data = response.data as IApiErrorResponse | undefined;
  if (data?.errors && Array.isArray(data.errors)) {
    return data.errors;
  }
  return [];
}

/**
 * Extract the field name from a property path
 * e.g., "Sections[0].Questions[1].Text" -> "Text"
 * e.g., "Name" -> "Name"
 */
function extractFieldName(propertyName: string | undefined): string | null {
  if (!propertyName) {
    return null;
  }

  const parts = propertyName.split(".");
  const lastPart = parts[parts.length - 1];

  // Remove array indices like [0]
  return lastPart.replace(/\[\d+\]/g, "");
}

/**
 * Format a field name for display (convert camelCase/PascalCase to readable format)
 * e.g., "Text" -> "Text", "SurveyName" -> "Survey Name"
 */
function formatFieldNameForDisplay(fieldName: string): string {
  // Add space before capital letters and trim
  return fieldName.replace(/([A-Z])/g, " $1").trim();
}

/**
 * Format an error as "[X] field is required" if applicable
 */
export function formatRequiredFieldError(error: IApiError): string {
  const isRequiredError =
    error.code === "NotEmptyValidator" ||
    error.message.toLowerCase().includes("must not be empty") ||
    error.message.toLowerCase().includes("required");

  if (isRequiredError && error.propertyName) {
    const fieldName = extractFieldName(error.propertyName);
    if (fieldName) {
      return i18next.t("common_fieldRequired", {
        field: formatFieldNameForDisplay(fieldName),
      });
    }
  }

  return error.message;
}

/**
 * Get a user-friendly summary error message for toast display
 */
export function getSummaryErrorMessage(errors: IApiError[]): string {
  if (errors.length === 0) {
    return i18next.t("common_saveError");
  }

  // Format the first error properly
  const firstError = errors[0];
  const formattedError = formatRequiredFieldError(firstError);

  if (formattedError) {
    return formattedError;
  }

  return i18next.t("common_saveError");
}

/**
 * Parse a property path like "Sections[0].Questions[1].Text" into indices
 * Returns an object with section index, question index, and field name
 */
export function parsePropertyPath(propertyName: string | undefined): {
  sectionIndex?: number;
  questionIndex?: number;
  field?: string;
} | null {
  if (!propertyName) {
    return null;
  }

  const sectionMatch = propertyName.match(/Sections\[(\d+)\]/);
  const questionMatch = propertyName.match(/Questions\[(\d+)\]/);

  const parts = propertyName.split(".");
  const field = parts[parts.length - 1];

  return {
    sectionIndex: sectionMatch ? parseInt(sectionMatch[1], 10) : undefined,
    questionIndex: questionMatch ? parseInt(questionMatch[1], 10) : undefined,
    field: field || undefined,
  };
}
